// Domain command services. Each command is a pure function:
//   (view of the workspace, input, context) → Changeset
// No storage, no UI, no globals. The store commits changesets transactionally;
// the same commands run in the desktop app, tests and (later) the sync server.
import { newId } from './ids.ts';
import { addDays, addMonths, type Day } from './dates.ts';
import type { Changeset, Put, WorkspaceView } from './changeset.ts';
import {
  type Case, type CaseRequirement, type CaseStage, type LocationType, type Note, type Outcome, type Person, type PersonStage,
  type Policy, type Priority, type Prospect, type ProspectList, type ProspectStatus, type RequirementCategory, type Review,
  type ReviewOutcome, type RowTypes, type ComparisonDecision, type Setting, type TableName, type Task, type TaskKind, type TimelineEvent, type TimelineType,
} from './schema.ts';
import { isTerminalStage, phoneMatchKey, searchKey, splitName } from './rules.ts';
import { regionPack } from './regions.ts';
import { dncKey, doNotCallKeys } from './calls.ts';
import { comparePlans, normalizePlans, type PlanInput } from './comparator.ts';
import { canTransition } from './policy-engine.ts';
import { checkCatalogLinks } from './catalog.ts';
import { policyProductionPuts } from './production.ts';
import { cleanReviewAnswers, reviewActions, portfolioAnnualOf } from './review-evidence.ts';

export { DomainError, type Ctx } from './cmd.ts';
import { DomainError, created, touched, put, clean, opt, timeline, requirePerson, type Ctx } from './cmd.ts';

// --- People ----------------------------------------------------------------------
export interface PersonInput {
  displayName?: string; firstName?: string; lastName?: string; stage?: PersonStage;
  primaryPhone?: string; primaryEmail?: string; birthDate?: string | null; occupation?: string; address?: string; city?: string;
  region?: string; source?: string; notes?: string; gender?: string; maritalStatus?: string; nameDay?: string | null; hot?: boolean;
  /** v7: since when a client (a day); referred by an existing person or a name */
  clientSince?: string | null; referredByPersonId?: string | null; referredByName?: string | null;
}

export function createPerson(v: WorkspaceView, input: PersonInput, ctx: Ctx): Changeset {
  const first = clean(input.firstName), last = clean(input.lastName);
  const display = clean(input.displayName) || [first, last].filter(Boolean).join(' ');
  if (!display) throw new DomainError('person.name_required');
  const names = first || last ? { firstName: first || null, lastName: last || null } : splitName(display);
  const stage = input.stage ?? 'client';
  const person = created<'persons'>(ctx, {
    kind: stage === 'client' || stage === 'former' ? 'client' : stage === 'dependent' ? 'dependent' : 'prospect',
    stage, displayName: display, firstName: names.firstName || null, lastName: names.lastName || null,
    primaryPhone: opt(input.primaryPhone), primaryEmail: opt(input.primaryEmail),
    phones: input.primaryPhone ? [{ label: 'mobile', value: clean(input.primaryPhone) }] : [],
    emails: input.primaryEmail ? [{ label: 'personal', value: clean(input.primaryEmail) }] : [],
    birthDate: input.birthDate || null, occupation: opt(input.occupation), address: opt(input.address), city: opt(input.city),
    region: opt(input.region), source: opt(input.source), notes: opt(input.notes), gender: opt(input.gender),
    maritalStatus: opt(input.maritalStatus), nameDay: input.nameDay || null, hot: input.hot ?? false, tags: [],
    ...(input.clientSince ? { clientSince: validDay(input.clientSince, ctx) } : {}),
    ...(input.referredByPersonId ? { referredByPersonId: requirePerson(v, input.referredByPersonId).id } : {}),
    ...(clean(input.referredByName) ? { referredByName: clean(input.referredByName) } : {}),
  });
  return { action: 'person.create', entity: 'persons', entityId: person.id, summary: display,
    puts: [put('persons', person), timeline(ctx, person.id, 'created', 'timeline.person_created', null, { table: 'persons', id: person.id })], deletes: [] };
}

export function updatePerson(v: WorkspaceView, id: string, patch: Partial<Person>, ctx: Ctx): Changeset {
  const p = requirePerson(v, id);
  const next = touched(ctx, p, sanitizePatch(patch));
  if (patch.primaryPhone !== undefined) {
    const phones = (p.phones ?? []).filter((x) => x.value !== p.primaryPhone);
    next.phones = patch.primaryPhone ? [{ label: 'mobile', value: patch.primaryPhone }, ...phones] : phones;
  }
  if (patch.primaryEmail !== undefined) {
    const emails = (p.emails ?? []).filter((x) => x.value !== p.primaryEmail);
    next.emails = patch.primaryEmail ? [{ label: 'personal', value: patch.primaryEmail }, ...emails] : emails;
  }
  if (patch.stage) next.kind = patch.stage === 'client' || patch.stage === 'former' ? 'client' : patch.stage === 'dependent' ? 'dependent' : 'prospect';
  if (patch.clientSince) next.clientSince = validDay(patch.clientSince, ctx);
  // becoming a client today records the day, unless the advisor gave one
  if (patch.stage === 'client' && p.stage !== 'client' && !next.clientSince) next.clientSince = ctx.today;
  if (patch.referredByPersonId) requirePerson(v, patch.referredByPersonId);
  return { action: 'person.update', entity: 'persons', entityId: id, summary: next.displayName, puts: [put('persons', next)], deletes: [] };
}

/** a calendar day (YYYY-MM-DD), not in the future */
function validDay(d: string, ctx: Ctx): string {
  const day = String(d).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || Number.isNaN(Date.parse(day))) throw new DomainError('person.client_since_invalid');
  if (day > ctx.today) throw new DomainError('person.client_since_future');
  return day;
}
/** the day a person becomes a client because of a policy: its issue/start date when known and not in the future */
function becameClientOn(pol: Pick<Policy, 'issueDate' | 'startDate'>, ctx: Ctx): string {
  const d = (pol.issueDate || pol.startDate || '').slice(0, 10);
  return d && d <= ctx.today ? d : ctx.today;
}

function sanitizePatch<T extends object>(patch: Partial<T>): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const [k, val] of Object.entries(patch)) {
    if (['id', 'createdAt', 'updatedAt', 'version', 'legacy'].includes(k)) continue;
    out[k] = typeof val === 'string' ? (val.trim() === '' ? null : val.trim()) : val;
  }
  return out as Partial<T>;
}

export function archivePerson(v: WorkspaceView, id: string, ctx: Ctx): Changeset {
  const p = requirePerson(v, id);
  return { action: 'person.archive', entity: 'persons', entityId: id, summary: p.displayName,
    puts: [put('persons', touched(ctx, p, { archivedAt: ctx.now })), timeline(ctx, id, 'system', 'timeline.person_archived')], deletes: [] };
}

// --- Notes -------------------------------------------------------------------------
export interface NoteInput { personId?: string | null; prospectId?: string | null; title?: string; body: string; dueDate?: string | null; dueTime?: string | null; priority?: Priority;
  /** action type of the dated note (spec §14): the task kind in the one task engine + an optional tag (waiting / service) */
  kind?: TaskKind; tag?: string | null }

/** v600 Advisor Flow: a note may carry a due date → it becomes a follow-up in the agenda. */
export function addNote(v: WorkspaceView, input: NoteInput, ctx: Ctx): Changeset {
  const body = clean(input.body);
  if (!body && !clean(input.title)) throw new DomainError('note.empty');
  if (input.personId) requirePerson(v, input.personId);
  const puts: Put[] = [];
  let taskId: string | null = null;
  if (input.dueDate) {
    const task = created<'tasks'>(ctx, {
      kind: input.kind && !(input.kind === 'appointment' && !input.dueTime) ? input.kind : 'followup', title: clean(input.title) || body.split('\n')[0]!.slice(0, 120), personId: input.personId ?? null, prospectId: input.prospectId ?? null,
      dueDate: input.dueDate, dueTime: input.dueTime || null, status: 'open', priority: input.priority ?? 'normal', notes: body, origin: 'note', ...(input.tag ? { data: { tag: input.tag } } : {}),
    });
    taskId = task.id;
    puts.push(put('tasks', task));
  }
  const note = created<'notes'>(ctx, { personId: input.personId ?? null, prospectId: input.prospectId ?? null, title: opt(input.title), body: body || clean(input.title), pinned: false, taskId });
  puts.push(put('notes', note));
  if (input.personId) puts.push(timeline(ctx, input.personId, 'note', clean(input.title) || 'timeline.note_added', body.slice(0, 280), { table: 'notes', id: note.id }));
  return { action: 'note.add', entity: 'notes', entityId: note.id, puts, deletes: [] };
}

export function updateNote(v: WorkspaceView, id: string, patch: Partial<Pick<Note, 'title' | 'body' | 'pinned'>>, ctx: Ctx): Changeset {
  const n = v.get('notes', id);
  if (!n) throw new DomainError('note.not_found');
  return { action: 'note.update', entity: 'notes', entityId: id, puts: [put('notes', touched<Note>(ctx, n, sanitizePatch<Note>(patch)))], deletes: [] };
}

// --- Tasks / follow-ups / appointments ------------------------------------------------
export interface TaskInput {
  kind: TaskKind; title: string; personId?: string | null; prospectId?: string | null; caseId?: string | null; policyId?: string | null;
  dueDate?: string | null; dueTime?: string | null; durationMin?: number | null; locationType?: LocationType | null; location?: string | null;
  priority?: Priority; notes?: string | null; origin?: string;
}

export function createTask(v: WorkspaceView, input: TaskInput, ctx: Ctx): Changeset {
  const title = clean(input.title);
  if (!title) throw new DomainError('task.title_required');
  if (input.kind === 'appointment' && (!input.dueDate || !input.dueTime)) throw new DomainError('task.appointment_needs_datetime');
  if (input.personId) requirePerson(v, input.personId);
  const task = created<'tasks'>(ctx, {
    kind: input.kind, title, personId: input.personId ?? null, prospectId: input.prospectId ?? null, caseId: input.caseId ?? null,
    policyId: input.policyId ?? null, dueDate: input.dueDate || null, dueTime: input.dueTime || null,
    durationMin: input.durationMin ?? (input.kind === 'appointment' ? 45 : null), locationType: input.locationType ?? null,
    location: opt(input.location), status: 'open', priority: input.priority ?? 'normal', notes: opt(input.notes), origin: input.origin ?? 'manual',
  });
  const puts: Put[] = [put('tasks', task)];
  if (task.personId) puts.push(timeline(ctx, task.personId, task.kind === 'appointment' ? 'appointment' : 'task', title, null, { table: 'tasks', id: task.id }));
  return { action: 'task.create', entity: 'tasks', entityId: task.id, summary: title, puts, deletes: [] };
}

export function updateTask(v: WorkspaceView, id: string, patch: Partial<Task>, ctx: Ctx): Changeset {
  const t = v.get('tasks', id);
  if (!t) throw new DomainError('task.not_found');
  return { action: 'task.update', entity: 'tasks', entityId: id, summary: t.title, puts: [put('tasks', touched(ctx, t, sanitizePatch(patch)))], deletes: [] };
}

export function completeTask(v: WorkspaceView, id: string, input: { outcome?: Outcome; note?: string }, ctx: Ctx): Changeset {
  const t = v.get('tasks', id);
  if (!t) throw new DomainError('task.not_found');
  const done = touched(ctx, t, { status: 'done', completedAt: ctx.now, outcome: input.outcome ?? null, notes: [t.notes, clean(input.note)].filter(Boolean).join('\n') || null });
  const puts: Put[] = [put('tasks', done)];
  if (t.personId) puts.push(timeline(ctx, t.personId, t.kind === 'call' ? 'call' : t.kind === 'appointment' ? 'meeting' : 'task', t.title, clean(input.note) || null, { table: 'tasks', id }));
  return { action: 'task.complete', entity: 'tasks', entityId: id, summary: t.title, puts, deletes: [] };
}

/** Flow mode: record a contact that just happened (call / message / meeting) as one atomic change —
 *  a completed task with its outcome plus the timeline entry. Resets "silence" for the relationship. */
export function logContact(v: WorkspaceView, input: { personId: string; kind?: 'call' | 'message' | 'meeting'; outcome: Outcome; title: string; note?: string }, ctx: Ctx): Changeset {
  requirePerson(v, input.personId);
  const title = clean(input.title);
  if (!title) throw new DomainError('task.title_required');
  const kind = input.kind ?? 'call';
  const task = created<'tasks'>(ctx, {
    kind: kind === 'meeting' ? 'appointment' : kind === 'message' ? 'followup' : 'call', title, personId: input.personId, prospectId: null, caseId: null, policyId: null,
    dueDate: ctx.today, dueTime: null, durationMin: null, locationType: null, location: null, status: 'done', priority: 'normal',
    notes: opt(input.note), origin: 'flow', completedAt: ctx.now, outcome: input.outcome,
  });
  return {
    action: 'contact.log', entity: 'tasks', entityId: task.id, summary: title,
    puts: [put('tasks', task), timeline(ctx, input.personId, kind, title, clean(input.note) || null, { table: 'tasks', id: task.id })], deletes: [],
  };
}

/** Move a task to another day (evening review). The first due date and every move stay on the task; this is not a completion. */
export function rescheduleTask(v: WorkspaceView, id: string, newDate: Day, ctx: Ctx, reason?: string): Changeset {
  const t = v.get('tasks', id);
  if (!t) throw new DomainError('task.not_found');
  if (t.status !== 'open') throw new DomainError('task.not_open');
  if (!newDate || newDate === t.dueDate) throw new DomainError('task.same_date');
  const moves = [...(((t.data as { moves?: unknown[] } | undefined)?.moves) ?? []), { from: t.dueDate ?? null, to: newDate, at: ctx.now, reason: reason ?? null }];
  const next = touched(ctx, t, { dueDate: newDate, originalDueDate: t.originalDueDate ?? t.dueDate ?? null, rescheduleCount: (t.rescheduleCount ?? 0) + 1, data: { ...(t.data ?? {}), moves } });
  return { action: 'task.reschedule', entity: 'tasks', entityId: id, summary: t.title, puts: [put('tasks', next)], deletes: [] };
}

export function reopenTask(v: WorkspaceView, id: string, ctx: Ctx): Changeset {
  const t = v.get('tasks', id);
  if (!t) throw new DomainError('task.not_found');
  return { action: 'task.reopen', entity: 'tasks', entityId: id, puts: [put('tasks', touched(ctx, t, { status: 'open', completedAt: null, outcome: null }))], deletes: [] };
}

export function deleteTask(v: WorkspaceView, id: string): Changeset {
  const t = v.get('tasks', id);
  if (!t) throw new DomainError('task.not_found');
  // v14: a planner block for this task stays as the advisor's own block, without the link
  const puts: Put[] = v.where('plan_blocks', 'taskId', id).map((b) => put('plan_blocks', { ...b, taskId: null, updatedAt: new Date().toISOString(), version: b.version + 1 }));
  return { action: 'task.delete', entity: 'tasks', entityId: id, summary: t.title, puts, deletes: [{ table: 'tasks', id }] };
}

// --- Policies ----------------------------------------------------------------------
export type PolicyInput = Omit<Policy, 'id' | 'createdAt' | 'updatedAt' | 'version' | 'data' | 'legacy'> & { id?: string };

export function savePolicy(v: WorkspaceView, input: PolicyInput, ctx: Ctx): Changeset {
  requirePerson(v, input.personId);
  if (!clean(input.productName)) throw new DomainError('policy.product_required');
  const existing = input.id ? v.get('policies', input.id) : undefined;
  if (existing?.deletedAt) throw new DomainError('policy.deleted');
  // v13: the deletion fields are written only by deletePolicy/restorePolicy (server step-up), never by the form
  const { deletedAt: _d, deletedBy: _b, deleteReason: _r, ...fields } = input;
  const row = existing ? touched(ctx, existing, sanitizePatch(fields as Partial<Policy>)) : created<'policies'>(ctx, { ...fields, annualPremium: Number(input.annualPremium) || 0 });
  if (existing && existing.status !== row.status && !canTransition(existing.status, row.status)) throw new DomainError('policy.invalid_transition');
  if (row.annualPremium < 0) throw new DomainError('policy.negative_premium');
  // v14: catalogue links must be one consistent Insurer → Category → Plan path of this workspace's catalogue
  checkCatalogLinks(v, row);
  const puts: Put[] = [put('policies', row)];
  if (!existing) puts.push(timeline(ctx, input.personId, 'policy', row.productName, [row.carrier, row.policyNumber].filter(Boolean).join(' · ') || null, { table: 'policies', id: row.id }));
  // a person with an active policy is a client
  const person = v.get('persons', input.personId)!;
  if ((row.status === 'active' || row.status === 'renewed') && person.stage !== 'client') {
    puts.push(put('persons', touched(ctx, person, { stage: 'client', kind: 'client', ...(person.clientSince ? {} : { clientSince: becameClientOn(row, ctx) }) })));
  }
  if (existing && existing.status !== row.status) puts.push(timeline(ctx, row.personId, 'policy', `timeline.policy_${row.status}`, row.productName, { table: 'policies', id: row.id }));
  puts.push(...policyProductionPuts(v, existing, row, ctx));
  return { action: existing ? 'policy.update' : 'policy.create', entity: 'policies', entityId: row.id, summary: row.productName, puts, deletes: [] };
}

// --- Cases (Application Journey) -------------------------------------------------------
export interface CaseInput {
  personId: string; product: string; insurer?: string; applicationNumber?: string; stage?: CaseStage; priority?: Priority;
  premium?: number | null; sumAssured?: number | null; targetDate?: string | null; nextAction?: string; nextActionDate?: string | null;
  nextActionTime?: string | null; notes?: string; checklist?: RequirementCategory[];
  /** v5: product category and an insurer/product checklist template to start from (instead of the market default) */
  category?: string | null; templateId?: string | null;
}

/** v900 syncTask(): exactly one open task mirrors a case's next action; terminal stages close it. */
function syncCaseTask(v: WorkspaceView, c: Case, person: Person, ctx: Ctx, pendingPuts: Put[]): Put[] {
  const existing = v.where('tasks', 'caseId', c.id).find((t) => t.status === 'open' && t.origin === 'case');
  if (isTerminalStage(c.stage)) {
    return existing ? [put('tasks', touched(ctx, existing, { status: 'done', completedAt: ctx.now }))] : [];
  }
  if (!c.nextActionDate) return [];
  const title = c.nextAction ? `${c.nextAction} · ${c.product}` : c.product;
  const fields = { title, personId: person.id, dueDate: c.nextActionDate, dueTime: c.nextActionTime || '09:00', priority: c.priority, kind: 'application' as TaskKind, caseId: c.id };
  if (existing) return [put('tasks', touched(ctx, existing, fields))];
  if (pendingPuts.some((p) => p.table === 'tasks' && (p.row as Task).caseId === c.id)) return [];
  return [put('tasks', created<'tasks'>(ctx, { ...fields, status: 'open', origin: 'case', notes: c.notes ?? null }))];
}

export function createCase(v: WorkspaceView, input: CaseInput, ctx: Ctx): Changeset {
  const person = requirePerson(v, input.personId);
  if (!clean(input.product)) throw new DomainError('case.product_required');
  const stage = input.stage ?? 'draft';
  const c = created<'cases'>(ctx, {
    personId: person.id, product: clean(input.product), insurer: opt(input.insurer), applicationNumber: opt(input.applicationNumber),
    stage, priority: input.priority ?? 'normal', premium: input.premium ?? null, sumAssured: input.sumAssured ?? null,
    submittedAt: stage !== 'draft' ? ctx.today : null, targetDate: input.targetDate || null, nextAction: opt(input.nextAction),
    nextActionDate: input.nextActionDate || null, nextActionTime: input.nextActionTime || null, notes: opt(input.notes),
    closedAt: isTerminalStage(stage) ? ctx.now : null,
  });
  const tpl = input.templateId ? v.get('checklist_templates', input.templateId) : undefined;
  if (input.templateId && !tpl) throw new DomainError('checklist.not_found');
  if (input.category) c.category = clean(input.category) || null;
  const puts: Put[] = [put('cases', c)];
  if (tpl) {
    (Array.isArray(tpl.items) ? tpl.items as { category: RequirementCategory; label?: string | null; mandatory?: boolean }[] : []).forEach((it, i) => puts.push(put('case_requirements', created<'case_requirements'>(ctx, { caseId: c.id, category: it.category, label: it.label ?? null, done: false, sort: i, status: 'required', templateId: tpl.id, mandatory: it.mandatory !== false }))));
  } else {
    const checklist = input.checklist ?? regionPack(ctx.region).caseChecklist;
    checklist.forEach((cat, i) => puts.push(put('case_requirements', created<'case_requirements'>(ctx, { caseId: c.id, category: cat, label: null, done: false, sort: i }))));
  }
  puts.push(put('case_events', created<'case_events'>(ctx, { caseId: c.id, at: ctx.now, stage, note: 'case.event.created' })));
  puts.push(timeline(ctx, person.id, 'case', c.product, `stage:${stage}`, { table: 'cases', id: c.id }));
  puts.push(...syncCaseTask(v, c, person, ctx, puts));
  if (person.stage === 'lead') puts.push(put('persons', touched(ctx, person, { stage: 'prospect', kind: 'prospect' })));
  return { action: 'case.create', entity: 'cases', entityId: c.id, summary: c.product, puts, deletes: [] };
}

export function updateCase(v: WorkspaceView, id: string, patch: Partial<Case>, ctx: Ctx, opts: { issuePolicy?: boolean } = {}): Changeset {
  const c = v.get('cases', id);
  if (!c) throw new DomainError('case.not_found');
  const person = requirePerson(v, c.personId);
  const changes = sanitizePatch(patch);
  const stageChanged = !!changes.stage && changes.stage !== c.stage;
  const next = touched(ctx, c, changes);
  if (stageChanged) {
    if (next.stage !== 'draft' && !c.submittedAt) next.submittedAt = ctx.today;
    next.closedAt = isTerminalStage(next.stage) ? ctx.now : null;
  }
  const puts: Put[] = [];
  if (stageChanged && next.stage === 'issued' && opts.issuePolicy !== false && !next.policyId) {
    const pol = created<'policies'>(ctx, {
      personId: person.id, branch: 'life', productName: next.product, carrier: next.insurer ?? null, policyNumber: next.applicationNumber ?? null,
      status: 'active', startDate: ctx.today, issueDate: ctx.today, annualPremium: next.premium ?? 0, sumAssured: next.sumAssured ?? null, caseId: next.id,
      paymentFrequency: 'monthly',
    });
    next.policyId = pol.id;
    puts.push(put('policies', pol), timeline(ctx, person.id, 'policy', pol.productName, 'timeline.policy_issued_from_case', { table: 'policies', id: pol.id }));
    puts.push(...policyProductionPuts(v, undefined, pol, ctx));
    if (person.stage !== 'client') puts.push(put('persons', touched(ctx, person, { stage: 'client', kind: 'client', ...(person.clientSince ? {} : { clientSince: ctx.today }) })));
  }
  puts.unshift(put('cases', next));
  if (stageChanged) {
    puts.push(put('case_events', created<'case_events'>(ctx, { caseId: id, at: ctx.now, stage: next.stage, note: patch.notes && patch.notes !== c.notes ? patch.notes : null })));
    puts.push(timeline(ctx, person.id, 'case', next.product, `stage:${next.stage}`, { table: 'cases', id }));
  }
  puts.push(...syncCaseTask(v, next, person, ctx, puts));
  return { action: stageChanged ? 'case.stage' : 'case.update', entity: 'cases', entityId: id, summary: `${next.product} → ${next.stage}`, puts, deletes: [] };
}

export function toggleRequirement(v: WorkspaceView, reqId: string, done: boolean, ctx: Ctx, note?: string): Changeset {
  const r = v.get('case_requirements', reqId);
  if (!r) throw new DomainError('requirement.not_found');
  const c = v.get('cases', r.caseId);
  // v5: the lifecycle status follows the tick (received ⇄ required/requested) so both views agree
  const status = done ? (r.status === 'waived' ? 'waived' : 'received') : (r.requestedAt ? 'requested' : 'required');
  const puts: Put[] = [put('case_requirements', touched(ctx, r, { done, doneAt: done ? ctx.now : null, note: note ?? r.note ?? null, status, receivedAt: done ? (r.receivedAt ?? ctx.today) : null }))];
  if (c) puts.push(put('cases', touched(ctx, c, {})));
  return { action: 'case.requirement', entity: 'case_requirements', entityId: reqId, puts, deletes: [] };
}

export function addRequirement(v: WorkspaceView, caseId: string, category: RequirementCategory, label: string, ctx: Ctx): Changeset {
  const c = v.get('cases', caseId);
  if (!c) throw new DomainError('case.not_found');
  const sort = v.where('case_requirements', 'caseId', caseId).length;
  return { action: 'case.requirement.add', entity: 'cases', entityId: caseId, deletes: [],
    puts: [put('case_requirements', created<'case_requirements'>(ctx, { caseId, category, label: opt(label), done: false, sort })), put('cases', touched(ctx, c, {}))] };
}

// --- Reviews (Review Center 360) ------------------------------------------------------
export interface ReviewInput { personId: string; date?: string; outcome: ReviewOutcome; notes?: string; nextReviewDate?: string | null; followUpDate?: string | null;
  /** v5: the review questionnaire (question → yes/no/n.a. + note); answers that call for action become tasks */
  answers?: Record<string, { a: 'yes' | 'no' | 'na'; note?: string | null }> | null; actionTasks?: boolean }

export function completeReview(v: WorkspaceView, input: ReviewInput, ctx: Ctx): Changeset {
  const person = requirePerson(v, input.personId);
  const date = input.date || ctx.today;
  const next = input.nextReviewDate || addMonths(date, 12);
  const answers = cleanReviewAnswers(input.answers);
  const review = created<'reviews'>(ctx, { personId: person.id, date, nextReviewDate: next, outcome: input.outcome, notes: opt(input.notes), answers, portfolioAnnual: portfolioAnnualOf(v, person.id, ctx.today) });
  const puts: Put[] = [
    put('reviews', review),
    put('persons', touched(ctx, person, { lastReviewDate: date, nextReviewDate: next })),
    timeline(ctx, person.id, 'review', 'timeline.review_completed', opt(input.notes), { table: 'reviews', id: review.id }),
  ];
  // close any open review tasks for this person
  for (const t of v.where('tasks', 'personId', person.id)) if (t.status === 'open' && t.kind === 'review') puts.push(put('tasks', touched(ctx, t, { status: 'done', completedAt: ctx.now })));
  if (input.actionTasks !== false) for (const q of reviewActions(answers)) {
    puts.push(put('tasks', created<'tasks'>(ctx, { kind: 'followup', title: `review.action.${q}`, personId: person.id, dueDate: addDays(ctx.today, 7), dueTime: null, status: 'open', priority: 'normal', origin: 'review', notes: answers?.[q]?.note ?? null })));
  }
  if (input.followUpDate) {
    puts.push(put('tasks', created<'tasks'>(ctx, { kind: 'followup', title: 'review.followup_title', personId: person.id, dueDate: input.followUpDate, dueTime: '09:00', status: 'open', priority: 'normal', origin: 'review', notes: opt(input.notes) })));
  }
  return { action: 'review.complete', entity: 'reviews', entityId: review.id, summary: person.displayName, puts, deletes: [] };
}

// --- Prospects (lists, calling, conversion) ---------------------------------------------
export function createProspectList(v: WorkspaceView, input: { name: string; source?: string; description?: string }, ctx: Ctx): Changeset {
  if (!clean(input.name)) throw new DomainError('prospect_list.name_required');
  const list = created<'prospect_lists'>(ctx, { name: clean(input.name), source: opt(input.source), description: opt(input.description), archived: false });
  return { action: 'prospect_list.create', entity: 'prospect_lists', entityId: list.id, summary: list.name, puts: [put('prospect_lists', list)], deletes: [] };
}

export interface ProspectRowInput { name: string; phone?: string; email?: string; profession?: string; company?: string; area?: string; source?: string; notes?: string }
export type DuplicateKind = 'unique' | 'duplicate_in_list' | 'existing_client' | 'duplicate_in_batch' | 'do_not_call';

/** v313/v314 resilient import with v250 duplicate visualisation (green = unique, red = duplicate, flag existing client). */
export function classifyProspects(v: WorkspaceView, listId: string, rows: ProspectRowInput[]): { row: ProspectRowInput; kind: DuplicateKind; matchId?: string }[] {
  const inList = new Map<string, string>();
  for (const p of v.where('prospects', 'listId', listId)) { const k = phoneMatchKey(p.phone) || searchKey(p.name); if (k) inList.set(k, p.id); }
  const clients = new Map<string, string>();
  for (const p of v.rows('persons')) {
    const k1 = phoneMatchKey(p.primaryPhone); if (k1) clients.set(k1, p.id);
    if (p.primaryEmail) clients.set(p.primaryEmail.toLowerCase(), p.id);
  }
  const batch = new Set<string>();
  // v12: a number marked «do not call» anywhere (a prospect of any list, or a person) is never added again
  const dnc = doNotCallKeys(v);
  return rows.map((row) => {
    const pk = phoneMatchKey(row.phone), nk = searchKey(row.name), ek = clean(row.email).toLowerCase();
    const key = pk || nk;
    const dk = dncKey(row.phone);
    if (dk && dnc.has(dk)) return { row, kind: 'do_not_call' as const };
    if (key && inList.has(key)) return { row, kind: 'duplicate_in_list' as const, matchId: inList.get(key) };
    const cm = (pk && clients.get(pk)) || (ek && clients.get(ek));
    if (cm) return { row, kind: 'existing_client' as const, matchId: cm };
    if (key && batch.has(key)) return { row, kind: 'duplicate_in_batch' as const };
    if (key) batch.add(key);
    return { row, kind: 'unique' as const };
  });
}

export function addProspects(v: WorkspaceView, listId: string, rows: ProspectRowInput[], ctx: Ctx, opts: { skipDuplicates?: boolean } = {}): Changeset {
  const list = v.get('prospect_lists', listId);
  if (!list) throw new DomainError('prospect_list.not_found');
  const classified = classifyProspects(v, listId, rows.filter((r) => clean(r.name) || clean(r.phone)));
  const puts: Put[] = [];
  for (const { row, kind } of classified) {
    if (kind === 'do_not_call') continue;
    if (opts.skipDuplicates !== false && (kind === 'duplicate_in_list' || kind === 'duplicate_in_batch')) continue;
    puts.push(put('prospects', created<'prospects'>(ctx, {
      listId, name: clean(row.name) || clean(row.phone), phone: opt(row.phone), email: opt(row.email), profession: opt(row.profession),
      company: opt(row.company), area: opt(row.area), source: opt(row.source) ?? list.source ?? null, priority: 'normal', status: 'new', notes: opt(row.notes),
      // v12: where the number came from and on what basis it is held (the list's defaults)
      lawfulBasis: list.lawfulBasis ?? null, consentSource: list.consentSource ?? list.source ?? null, consentDate: list.consentSource || list.lawfulBasis ? ctx.today : null,
    })));
  }
  puts.push(put('prospect_lists', touched(ctx, list, {})));
  return { action: 'prospect.import', entity: 'prospect_lists', entityId: listId, summary: `${puts.length - 1}`, puts, deletes: [] };
}

export function setProspectStatus(v: WorkspaceView, id: string, input: { status: ProspectStatus; note?: string; followUpDate?: string | null; followUpTime?: string | null }, ctx: Ctx): Changeset {
  const p = v.get('prospects', id);
  if (!p) throw new DomainError('prospect.not_found');
  const notes = clean(input.note) ? [p.notes, `${ctx.today}: ${clean(input.note)}`].filter(Boolean).join('\n') : p.notes;
  const next = touched(ctx, p, {
    status: input.status, notes: notes ?? null, lastCallDate: ctx.today,
    nextFollowUpDate: input.followUpDate ?? (input.status === 'callback' ? p.nextFollowUpDate : p.nextFollowUpDate),
    appointmentDate: input.status === 'appointment' ? input.followUpDate ?? p.appointmentDate : p.appointmentDate,
  });
  const puts: Put[] = [put('prospects', next)];
  if (input.followUpDate && (input.status === 'callback' || input.status === 'interested' || input.status === 'appointment')) {
    puts.push(put('tasks', created<'tasks'>(ctx, {
      kind: input.status === 'appointment' ? 'appointment' : 'call', title: p.name, prospectId: p.id, dueDate: input.followUpDate,
      dueTime: input.followUpTime || (input.status === 'appointment' ? '10:00' : '09:00'), durationMin: input.status === 'appointment' ? 45 : null,
      status: 'open', priority: input.status === 'interested' ? 'high' : 'normal', origin: 'prospect', notes: opt(input.note),
    })));
  }
  return { action: 'prospect.status', entity: 'prospects', entityId: id, summary: `${p.name} → ${input.status}`, puts, deletes: [] };
}

export function convertProspect(v: WorkspaceView, id: string, ctx: Ctx, stage: PersonStage = 'prospect'): Changeset {
  const p = v.get('prospects', id);
  if (!p) throw new DomainError('prospect.not_found');
  if (p.convertedPersonId && v.get('persons', p.convertedPersonId)) throw new DomainError('prospect.already_converted');
  const cs = createPerson(v, { displayName: p.name, primaryPhone: p.phone ?? undefined, primaryEmail: p.email ?? undefined, occupation: p.profession ?? undefined, city: p.area ?? undefined, source: p.source ?? undefined, notes: p.notes ?? undefined, stage }, ctx);
  const personId = cs.entityId!;
  const puts: Put[] = [...cs.puts, put('prospects', touched(ctx, p, { status: 'converted', convertedPersonId: personId }))];
  // carry open prospect tasks over to the new person
  for (const t of v.rows('tasks')) if (t.prospectId === id && t.status === 'open') puts.push(put('tasks', touched(ctx, t, { personId })));
  for (const n of v.rows('notes')) if (n.prospectId === id) puts.push(put('notes', touched(ctx, n, { personId })));
  return { action: 'prospect.convert', entity: 'persons', entityId: personId, summary: p.name, puts, deletes: [] };
}

// --- Household -----------------------------------------------------------------------------
export function addHouseholdMember(v: WorkspaceView, personId: string, input: { name: string; birthDate?: string | null; role?: 'child' | 'spouse' | 'dependent' | 'other'; notes?: string }, ctx: Ctx): Changeset {
  const p = requirePerson(v, personId);
  if (!clean(input.name)) throw new DomainError('person.name_required');
  const puts: Put[] = [];
  let householdId = p.householdId;
  if (!householdId) {
    const h = created<'households'>(ctx, { name: p.displayName });
    householdId = h.id;
    puts.push(put('households', h), put('persons', touched(ctx, p, { householdId })));
    puts.push(put('household_members', created<'household_members'>(ctx, { householdId, personId: p.id, role: 'primary' })));
  }
  const role = input.role ?? 'child';
  const names = splitName(clean(input.name));
  const member = created<'persons'>(ctx, {
    kind: 'dependent', stage: 'dependent', displayName: clean(input.name), firstName: names.firstName || null, lastName: names.lastName || null,
    birthDate: input.birthDate || null, householdId, notes: opt(input.notes),
  });
  puts.push(put('persons', member), put('household_members', created<'household_members'>(ctx, { householdId, personId: member.id, role })));
  puts.push(timeline(ctx, p.id, 'system', clean(input.name), `household:${role}`, { table: 'persons', id: member.id }));
  return { action: 'household.add', entity: 'persons', entityId: member.id, summary: member.displayName, puts, deletes: [] };
}

// --- Policy Comparator ---------------------------------------------------------------------
export interface ComparisonInput { id?: string; personId: string; title: string; plans: PlanInput[]; notes?: string }

export function saveComparison(v: WorkspaceView, input: ComparisonInput, ctx: Ctx): Changeset {
  const person = requirePerson(v, input.personId);
  const existing = input.id ? v.get('comparisons', input.id) : undefined;
  if (existing?.status === 'decided') throw new DomainError('comparison.locked');
  const plans = normalizePlans(input.plans);
  if (plans.length < 1) throw new DomainError('comparison.no_plans');
  const title = clean(input.title) || plans.map((p) => p.product || p.label).join(' vs ');
  const row = existing ? touched(ctx, existing, { title, plans, notes: opt(input.notes) }) : created<'comparisons'>(ctx, { personId: person.id, title, status: 'draft', plans, notes: opt(input.notes) });
  const puts: Put[] = [put('comparisons', row)];
  if (!existing) puts.push(timeline(ctx, person.id, 'comparison', title, null, { table: 'comparisons', id: row.id }));
  return { action: existing ? 'comparison.update' : 'comparison.create', entity: 'comparisons', entityId: row.id, summary: title, puts, deletes: [] };
}

/** Freeze inputs + computed results as an immutable snapshot (legacy v230 decision flow). */
export function decideComparison(v: WorkspaceView, id: string, input: { decision: ComparisonDecision; notes?: string; followUpDate?: string | null }, ctx: Ctx): Changeset {
  const c = v.get('comparisons', id);
  if (!c) throw new DomainError('comparison.not_found');
  if (c.status === 'decided') throw new DomainError('comparison.locked');
  const plans = normalizePlans(c.plans);
  const snapshot = { decidedAt: ctx.now, plans, verdict: comparePlans(plans), decision: input.decision };
  const puts: Put[] = [put('comparisons', touched(ctx, c, { status: 'decided', decision: input.decision, decidedAt: ctx.now, snapshot, notes: opt([c.notes, clean(input.notes)].filter(Boolean).join('\n')) }))];
  puts.push(timeline(ctx, c.personId, 'comparison', c.title, `decision:${input.decision}`, { table: 'comparisons', id }));
  // "decide later" always gets a follow-up (default one week), any decision may get one explicitly
  const followDate = input.followUpDate || (input.decision === 'later' ? addDays(ctx.today, 7) : null);
  if (followDate) {
    puts.push(put('tasks', created<'tasks'>(ctx, { kind: 'followup', title: c.title, personId: c.personId, dueDate: followDate, dueTime: '09:00', status: 'open', priority: 'high', origin: 'comparison', notes: opt(input.notes) })));
  }
  return { action: 'comparison.decide', entity: 'comparisons', entityId: id, summary: `${c.title} → ${input.decision}`, puts, deletes: [] };
}

/** Open a decided (or imported) comparison as a new draft — the original is never altered. */
export function copyComparison(v: WorkspaceView, id: string, ctx: Ctx): Changeset {
  const c = v.get('comparisons', id);
  if (!c) throw new DomainError('comparison.not_found');
  const row = created<'comparisons'>(ctx, { personId: c.personId, title: c.title, status: 'draft', plans: normalizePlans(c.plans), sourceComparisonId: c.id, notes: c.notes ?? null });
  return { action: 'comparison.copy', entity: 'comparisons', entityId: row.id, summary: c.title, puts: [put('comparisons', row)], deletes: [] };
}

// --- Settings ------------------------------------------------------------------------------
export function saveSetting(v: WorkspaceView, key: string, value: unknown, ctx: Ctx): Changeset {
  const existing = v.rows('settings').find((s) => s.key === key);
  const row: Setting = existing ? touched(ctx, existing, { value }) : created<'settings'>(ctx, { key, value });
  return { action: 'settings.save', entity: 'settings', entityId: row.id, summary: key, puts: [put('settings', row)], deletes: [] };
}
export function getSetting<T>(v: WorkspaceView, key: string, fallback: T): T {
  const s = v.rows('settings').find((x) => x.key === key);
  return (s?.value as T) ?? fallback;
}

export type { ProspectList, Prospect, Review, Note, TimelineEvent, CaseRequirement };

/** @internal shared command helpers for the other command modules (advisory.ts). */
export const _cmd = { created, touched, put, clean, opt, timeline, requirePerson };
